import { LogOut } from "lucide-react";
import { useEffect, useRef, type MouseEvent } from "react";
import styles from "./ConfirmExitDialog.module.css";

interface ConfirmExitDialogProps {
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmExitDialog({
  onCancel,
  onConfirm,
}: ConfirmExitDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const buttons = Array.from(
        dialogRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? [],
      );
      const firstButton = buttons[0];
      const lastButton = buttons.at(-1);

      if (
        event.shiftKey &&
        firstButton &&
        lastButton &&
        document.activeElement === firstButton
      ) {
        event.preventDefault();
        lastButton.focus();
      } else if (
        !event.shiftKey &&
        firstButton &&
        lastButton &&
        document.activeElement === lastButton
      ) {
        event.preventDefault();
        firstButton.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className={styles.backdrop} onMouseDown={handleBackdropClick}>
      <div
        className={styles.dialog}
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="exit-dialog-title"
        aria-describedby="exit-dialog-description"
      >
        <span className={styles.icon} aria-hidden="true">
          <LogOut />
        </span>
        <h2 id="exit-dialog-title">Avsluta spelet?</h2>
        <p id="exit-dialog-description">
          Den pågående rundan och alla resultat försvinner.
        </p>
        <div className={styles.actions}>
          <button ref={cancelButtonRef} type="button" onClick={onCancel}>
            Fortsätt spela
          </button>
          <button type="button" onClick={onConfirm}>
            Avsluta
          </button>
        </div>
      </div>
    </div>
  );
}
